import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, Building2, MapPin, Clock,
  RefreshCw, Trash2, MessageSquare, Loader2, Send, Calendar, UserPlus, AlertTriangle,
  History, Paperclip, Download,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import {
  PIPELINE_STAGES, STAGE_LABEL, STAGE_BADGE,
  formatFormType, formatDateTime, formatRelative, getDisplayName,
  isReadOnly, SPAM_REASON_LABEL, LEAD_TYPES, LEAD_TYPE_LABEL, LEAD_TYPE_BADGE,
} from "../utils";
import SpamReasonDialog from "../components/SpamReasonDialog";
import FollowUpControl from "../components/FollowUpControl";
import SLABubble from "../components/SLABubble";
import ActivityTimeline from "../components/ActivityTimeline";
import SampleRequestPanel from "../components/SampleRequestPanel";
import RequestUpdateDialog from "../components/RequestUpdateDialog";
import WonValuePanel from "../components/WonValuePanel";
import AttachmentLightbox from "../components/AttachmentLightbox";

// Fields we won't dump generically in "raw details" — they get their own UI.
const HIDDEN_FIELDS = new Set([
  "id", "_id", "name", "firstName", "lastName", "email", "phone", "company",
  "formType", "status", "notes", "audit_log", "timestamp", "created_at",
  "updated_at", "deleted_at", "assigned_to", "assigned_to_name",
  "hubspot_id", "score", "tags",
  "last_contacted_at", "subject", "message", "customerNotes",
  "spam_reason", "spam_reason_note", "spam_classified_at",
  "follow_up_due", "follow_up_preset", "follow_up_set_at", "follow_up_set_by",
  "follow_up_completed_at", "follow_up_overdue_notified_at",
  "follow_up_cancelled_reason", "sla_state",
  "lead_type", "final_won_value", "close_date",
  "file",  // rendered as its own AttachmentCard, not in raw dump
]);

const fieldLabel = (k) =>
  k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).replace(/_/g, " ");

export default function SubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useDashAuth();
  const readOnly = isReadOnly(user);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [spamOpen, setSpamOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dashApi.get(`/submissions/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Load assignable users once (Sales + Operations only — see backend ASSIGNABLE_ROLES)
  useEffect(() => {
    dashApi.get("/users/assignable")
      .then(({ data }) => setAssignees(data || []))
      .catch(() => setAssignees([]));
  }, []);

  const onStatusChange = async (newStatus) => {
    if (!doc || newStatus === doc.status) return;
    if (readOnly) return;
    // Spam requires the reason modal — short-circuit and open the dialog.
    if (newStatus === "spam") {
      setSpamOpen(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await dashApi.patch(`/submissions/${id}`, { status: newStatus });
      setDoc(data);
      // Phase B — soft revenue prompt when closing a deal won
      if (newStatus === "won" && !data.final_won_value) {
        toast.warning("Set the Final Won Value for accurate revenue reporting.", {
          duration: 6000,
        });
      } else {
        toast.success(`Status set to ${STAGE_LABEL[newStatus]}`);
      }
    } catch (e) {
      toast.error(e.message || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const onLeadTypeChange = async (e) => {
    const newType = e.target.value;
    if (readOnly || newType === doc.lead_type) return;
    setBusy(true);
    try {
      const { data } = await dashApi.patch(`/submissions/${id}`, { lead_type: newType });
      setDoc(data);
      toast.success(`Lead type set to ${LEAD_TYPE_LABEL[newType]}`);
    } catch (er) {
      toast.error(er.message || "Failed to update lead type");
    } finally {
      setBusy(false);
    }
  };

  const onWonValueSave = async (value, dateIso) => {
    if (readOnly) return;
    setBusy(true);
    try {
      const body = { final_won_value: Number(value) };
      if (dateIso) body.close_date = dateIso;
      const { data } = await dashApi.patch(`/submissions/${id}`, body);
      setDoc(data);
      toast.success("Won value updated");
    } catch (er) {
      toast.error(er.message || "Failed to update value");
    } finally {
      setBusy(false);
    }
  };

  const onConfirmSpam = async ({ spam_reason, spam_reason_note }) => {
    setBusy(true);
    try {
      const { data } = await dashApi.patch(`/submissions/${id}`, {
        status: "spam",
        spam_reason,
        spam_reason_note,
      });
      setDoc(data);
      setSpamOpen(false);
      toast.success("Marked as Spam");
    } catch (e) {
      toast.error(e.message || "Failed to mark as spam");
    } finally {
      setBusy(false);
    }
  };

  const onAssignedChange = async (e) => {
    const newId = e.target.value; // "" = unassign
    if ((doc.assigned_to || "") === newId) return;
    if (readOnly) return;
    setBusy(true);
    try {
      const { data } = await dashApi.patch(`/submissions/${id}`, { assigned_to: newId });
      setDoc(data);
      toast.success(newId ? "Assigned" : "Assignment cleared");
    } catch (er) {
      toast.error(er.message || "Failed to assign");
    } finally {
      setBusy(false);
    }
  };

  // ── Follow-up handlers ───────────────────────────────────────────
  const onSetFollowUp = async (body) => {
    try {
      const { data } = await dashApi.patch(`/submissions/${id}/follow-up`, body);
      setDoc(data);
      toast.success("Follow-up scheduled");
    } catch (er) {
      toast.error(er.message || "Failed to set follow-up");
    }
  };

  const onCompleteFollowUp = async () => {
    try {
      const { data } = await dashApi.post(`/submissions/${id}/follow-up/complete`);
      setDoc(data);
      toast.success("Follow-up marked complete");
    } catch (er) {
      toast.error(er.message || "Failed to complete follow-up");
    }
  };

  const onCancelFollowUp = async () => {
    try {
      const { data } = await dashApi.delete(`/submissions/${id}/follow-up`);
      setDoc(data);
      toast.success("Follow-up cancelled");
    } catch (er) {
      toast.error(er.message || "Failed to cancel follow-up");
    }
  };

  const onAddNote = async (e) => {
    e.preventDefault();
    const text = noteText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await dashApi.post(`/submissions/${id}/notes`, { text });
      setNoteText("");
      toast.success("Note added");
      await load();
    } catch (er) {
      toast.error(er.message || "Failed to add note");
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (!window.confirm("Re-send the original notification email for this submission?")) return;
    setBusy(true);
    try {
      await dashApi.post(`/submissions/${id}/resend-email`);
      toast.success("Email queued for resend");
      await load();
    } catch (er) {
      toast.error(er.message || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  const onSoftDelete = async () => {
    if (!window.confirm("Soft-delete this submission? It can be restored by an admin.")) return;
    setBusy(true);
    try {
      await dashApi.delete(`/submissions/${id}`);
      toast.success("Submission soft-deleted");
      navigate("/dashboard/submissions", { replace: true });
    } catch (er) {
      toast.error(er.message || "Delete failed");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading submission…
      </div>
    );
  }
  if (err || !doc) {
    return <div className="text-rose-700 bg-rose-50 border border-rose-200 p-4 rounded-md">{err || "Not found"}</div>;
  }

  const displayName = getDisplayName(doc);
  const extraFields = Object.entries(doc).filter(
    ([k, v]) => !HIDDEN_FIELDS.has(k) && v !== null && v !== undefined && v !== "" &&
                !(Array.isArray(v) && v.length === 0)
  );

  return (
    <div className="space-y-6" data-testid="submission-detail-page">
      <Link
        to="/dashboard/submissions"
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        data-testid="submission-back-link"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to submissions
      </Link>

      {/* Header */}
      <Card className="p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {doc.is_hot && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200"
                  title={doc.hot_reason || "High-intent lead"}
                  data-testid="hot-badge"
                >
                  🔥 HOT
                </span>
              )}
              <h1 className="text-2xl font-heading font-bold text-slate-900">{displayName}</h1>
              <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${STAGE_BADGE[doc.status] || "bg-slate-100"}`} data-testid="submission-status-badge">
                {STAGE_LABEL[doc.status] || doc.status || "new"}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {formatFormType(doc.formType)}
              </span>
              <span
                className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${LEAD_TYPE_BADGE[doc.lead_type || "new_prospect"]}`}
                data-testid="submission-lead-type-badge"
              >
                {LEAD_TYPE_LABEL[doc.lead_type || "new_prospect"]}
              </span>
              {doc.sla_state && (
                <SLABubble state={doc.sla_state} dueIso={doc.follow_up_due} testid="header-sla-bubble" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
              {doc.email && (
                <a href={`mailto:${doc.email}`} className="inline-flex items-center gap-1.5 hover:text-blue-700">
                  <Mail className="h-3.5 w-3.5" /> {doc.email}
                </a>
              )}
              {doc.phone && (
                <a href={`tel:${doc.phone}`} className="inline-flex items-center gap-1.5 hover:text-blue-700">
                  <Phone className="h-3.5 w-3.5" /> {doc.phone}
                </a>
              )}
              {doc.company && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" /> {doc.company}
                </span>
              )}
              {(doc.city || doc.state) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {[doc.city, doc.state].filter(Boolean).join(", ")}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {formatDateTime(doc.created_at || doc.timestamp)}
              </span>
            </div>
          </div>

          {/* Status + actions */}
          <div className="flex flex-col gap-2 min-w-[220px]">
            <label className="text-xs font-medium text-slate-600">Pipeline stage</label>
            <select
              value={doc.status || "new"}
              onChange={(e) => onStatusChange(e.target.value)}
              disabled={busy || readOnly}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
              data-testid="submission-status-select"
            >
              {PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>{STAGE_LABEL[s]}</option>
              ))}
            </select>

            <label className="text-xs font-medium text-slate-600 mt-1 inline-flex items-center gap-1">
              <UserPlus className="h-3.5 w-3.5" /> Assigned to
            </label>
            <select
              value={doc.assigned_to || ""}
              onChange={onAssignedChange}
              disabled={busy || readOnly}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
              data-testid="submission-assigned-select"
            >
              <option value="">— Unassigned —</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>

            <label className="text-xs font-medium text-slate-600 mt-1">Lead type</label>
            <select
              value={doc.lead_type || "new_prospect"}
              onChange={onLeadTypeChange}
              disabled={busy || readOnly}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
              data-testid="submission-lead-type-select"
            >
              {LEAD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setReqOpen(true)}
                disabled={busy || readOnly}
                data-testid="submission-request-update-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" /> Request update
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onResend}
                disabled={busy || readOnly}
                data-testid="submission-resend-btn"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Resend email
              </Button>
              {user?.role === "admin" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onSoftDelete}
                  disabled={busy}
                  className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                  data-testid="submission-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              )}
            </div>
            {readOnly && (
              <p className="text-[11px] text-slate-500 mt-1 italic">Read-only mode</p>
            )}
          </div>
        </div>
        {doc.status === "spam" && doc.spam_reason && (
          <div
            className="mt-4 flex items-start gap-3 p-3 rounded-md bg-rose-50 border border-rose-200"
            data-testid="spam-reason-banner"
          >
            <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <span className="font-medium text-rose-900">Spam:</span>{" "}
              <span className="text-rose-800">{SPAM_REASON_LABEL[doc.spam_reason] || doc.spam_reason}</span>
              {doc.spam_reason_note && (
                <span className="text-rose-700"> — {doc.spam_reason_note}</span>
              )}
            </div>
          </div>
        )}
        {doc.status === "won" && (
          <WonValuePanel
            doc={doc}
            onSave={onWonValueSave}
            disabled={readOnly}
            busy={busy}
          />
        )}
      </Card>

      {/* Subject + message + extra fields */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(doc.subject || doc.message || doc.customerNotes) && (
            <Card className="p-5">
              {doc.subject && <h2 className="font-heading font-semibold text-slate-900 mb-1">{doc.subject}</h2>}
              {doc.message && (
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="submission-message">
                  {doc.message}
                </p>
              )}
              {doc.customerNotes && (
                <div className={doc.message ? "mt-4 pt-4 border-t border-slate-100" : ""} data-testid="submission-customer-notes">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Customer note</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {doc.customerNotes}
                  </p>
                </div>
              )}
            </Card>
          )}

          {(() => {
            // Normalize legacy singleton `doc.file` + future `doc.files[]`
            // into a single array so the CRM is gallery-ready the moment
            // multi-file uploads arrive on any form.
            const raw = [
              ...(doc.file && doc.file.storage_path ? [doc.file] : []),
              ...(Array.isArray(doc.files) ? doc.files.filter((f) => f && f.storage_path) : []),
            ];
            if (raw.length === 0) return null;
            const attachments = raw.map((f, i) => {
              const ct = (f.content_type || "").toLowerCase();
              const kind = ct.startsWith("image/") ? "image"
                : (ct === "application/pdf" || (f.filename || "").toLowerCase().endsWith(".pdf")) ? "pdf"
                : "other";
              // Legacy singleton uses the plain endpoint; multi-file
              // attachments will use ?index=N (backend can wire this up
              // when the data model grows).
              const url = i === 0 && !Array.isArray(doc.files)
                ? `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/submissions/${doc.id}/attachment`
                : `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/submissions/${doc.id}/attachment?index=${i}`;
              const sizeLabel = typeof f.size === "number"
                ? (f.size < 1024 ? `${f.size} B`
                  : f.size < 1_048_576 ? `${Math.round(f.size / 1024)} KB`
                  : `${(f.size / 1_048_576).toFixed(1)} MB`)
                : null;
              return { url, filename: f.filename || `attachment-${i + 1}`, kind, sizeLabel };
            });
            const previewable = attachments.filter((a) => a.kind !== "other");
            const primary = attachments[0];
            const isMulti = attachments.length > 1;
            const openLightbox = (i) => {
              // Map the click on attachment `i` back to its index in the
              // previewable slice (lightbox only iterates previewables).
              const pIdx = previewable.findIndex((p) => p.url === attachments[i].url);
              setLightboxIndex(Math.max(0, pIdx));
              setLightboxOpen(true);
            };
            return (
              <Card className="p-5" data-testid="submission-attachment-card">
                <h3 className="font-heading font-semibold text-slate-900 mb-3 inline-flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  {isMulti ? `Attachments (${attachments.length})` : "Attachment"}
                </h3>

                {/* Primary preview — clicking opens the lightbox on that one */}
                {primary.kind === "image" && (
                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    data-testid="submission-attachment-image-preview"
                    className="block mb-3 w-full rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-slate-400 transition-colors cursor-zoom-in"
                    title="Click to expand"
                  >
                    <img
                      src={primary.url}
                      alt={primary.filename}
                      className="max-h-96 w-auto mx-auto"
                      loading="lazy"
                    />
                  </button>
                )}
                {primary.kind === "pdf" && (
                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    className="block w-full mb-3 rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-slate-400 transition-colors group cursor-zoom-in text-left"
                    data-testid="submission-attachment-pdf-preview"
                    title="Click to expand"
                  >
                    <iframe
                      src={primary.url}
                      title={primary.filename}
                      className="w-full h-[520px] pointer-events-none"
                    />
                    <div className="px-3 py-1.5 text-xs text-slate-500 bg-slate-100 border-t border-slate-200 group-hover:bg-slate-200">
                      Click to expand fullscreen{isMulti ? ` · ${attachments.length} total` : ""}
                    </div>
                  </button>
                )}

                {/* Thumbnail strip when there's more than one attachment */}
                {isMulti && (
                  <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3"
                    data-testid="submission-attachment-strip"
                  >
                    {attachments.map((a, i) => (
                      <button
                        key={a.url}
                        type="button"
                        onClick={() => a.kind === "other" ? window.open(a.url, "_blank") : openLightbox(i)}
                        className="flex flex-col items-stretch text-left rounded-md border border-slate-200 bg-slate-50 hover:border-slate-400 hover:bg-white transition-colors overflow-hidden"
                        data-testid={`submission-attachment-thumb-${i}`}
                        title={a.filename}
                      >
                        {a.kind === "image" ? (
                          <img src={a.url} alt={a.filename} className="w-full h-20 object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-20 flex items-center justify-center bg-slate-100 text-slate-500 text-xs uppercase tracking-wider">
                            {a.kind === "pdf" ? "PDF" : "File"}
                          </div>
                        )}
                        <div className="p-2 min-w-0">
                          <div className="text-xs font-medium text-slate-900 truncate">{a.filename}</div>
                          {a.sizeLabel && <div className="text-[10px] text-slate-500">{a.sizeLabel}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Download link(s) */}
                <div className={isMulti ? "flex flex-wrap gap-2" : ""}>
                  {(isMulti ? attachments : [primary]).map((a, i) => (
                    <a
                      key={a.url}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={i === 0 ? "submission-attachment-download" : `submission-attachment-download-${i}`}
                      className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-colors"
                    >
                      <Download className="h-4 w-4 text-slate-500" />
                      <span className="font-medium text-slate-900">{a.filename}</span>
                      {a.sizeLabel && <span className="text-xs text-slate-500">· {a.sizeLabel}</span>}
                    </a>
                  ))}
                </div>
              </Card>
            );
          })()}

          {extraFields.length > 0 && (
            <Card className="p-5" data-testid="submission-extra-fields">
              <h3 className="font-heading font-semibold text-slate-900 mb-3">Submission details</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {extraFields.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">{fieldLabel(k)}</dt>
                    <dd className="text-slate-800 mt-0.5 break-words">
                      {typeof v === "object" ? <pre className="text-xs">{JSON.stringify(v, null, 2)}</pre> : String(v)}
                    </dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}

          {/* Activity & Notes — unified vertical timeline */}
          <Card className="p-5" data-testid="activity-notes-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
                <History className="h-4 w-4" /> Activity & Notes
              </h3>
              <span className="text-xs text-slate-500">
                {(Array.isArray(doc.notes) ? doc.notes : []).length} note{(Array.isArray(doc.notes) ? doc.notes : []).length === 1 ? "" : "s"}
              </span>
            </div>

            <form onSubmit={onAddNote} className="space-y-2" data-testid="add-note-form">
              <Textarea
                placeholder={readOnly ? "Read-only users cannot add notes" : "Log a call, add context, share an update…"}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                maxLength={4000}
                disabled={readOnly}
                data-testid="note-textarea"
              />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={busy || readOnly || !noteText.trim()} data-testid="add-note-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Add note
                </Button>
              </div>
            </form>

            <div className="mt-5 pt-4 border-t border-slate-100">
              <ActivityTimeline doc={doc} />
            </div>
          </Card>

          {/* Sample-request fulfillment panel — appears only for sample_request submissions */}
          {doc.formType === "sample_request" && doc.sample_request_id && (
            <SampleRequestPanel
              sampleRequestId={doc.sample_request_id}
              readOnly={readOnly}
            />
          )}
        </div>

        {/* Right column — Follow-up only (activity merged into main column) */}
        <div className="space-y-6">
          <Card className="p-5">
            <FollowUpControl
              doc={doc}
              onSet={onSetFollowUp}
              onComplete={onCompleteFollowUp}
              onCancel={onCancelFollowUp}
              disabled={readOnly}
            />
          </Card>
        </div>
      </div>

      <SpamReasonDialog
        open={spamOpen}
        onCancel={() => setSpamOpen(false)}
        onConfirm={onConfirmSpam}
        submitting={busy}
      />
      <RequestUpdateDialog
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        doc={doc}
        onSent={() => load()}
      />
      {(() => {
        // Same normalization as the AttachmentCard — kept local so the
        // gallery lightbox works even if the render tree ever diverges.
        const raw = [
          ...(doc.file && doc.file.storage_path ? [doc.file] : []),
          ...(Array.isArray(doc.files) ? doc.files.filter((f) => f && f.storage_path) : []),
        ];
        if (raw.length === 0) return null;
        const items = raw
          .map((f, i) => {
            const ct = (f.content_type || "").toLowerCase();
            const kind = ct.startsWith("image/") ? "image"
              : (ct === "application/pdf" || (f.filename || "").toLowerCase().endsWith(".pdf")) ? "pdf"
              : "other";
            if (kind === "other") return null;
            const url = i === 0 && !Array.isArray(doc.files)
              ? `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/submissions/${doc.id}/attachment`
              : `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/submissions/${doc.id}/attachment?index=${i}`;
            return { url, filename: f.filename || `attachment-${i + 1}`, kind };
          })
          .filter(Boolean);
        if (items.length === 0) return null;
        return (
          <AttachmentLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            items={items}
            initialIndex={Math.min(lightboxIndex, items.length - 1)}
          />
        );
      })()}
    </div>
  );
}
